import { factory, Input } from './timelineWorker';
import { CellIndex, Timeline } from 'types';

const MaxWorkers = 4; // Adjust if necessary

export type Callback = (err: Error | null, timeline?: Timeline) => void;

export type Work = Input & {
    // Identify who the work is for
    cellIndex: CellIndex;
};
type WorkWithCallback = Work & {
    callback: Callback;
};

type TimelineWorker = Worker & {
    index: number;
};

export class TimelineWorkerPool {
    workInProgress: (WorkWithCallback | null)[];
    workers: TimelineWorker[]; // Array of all workers
    workerQueue: TimelineWorker[]; // Queue of idle/available workers
    workQueue: WorkWithCallback[]; // Queue of work waiting to be done

    constructor() {
        this.workers = [];
        this.workerQueue = [];
        this.workQueue = [];
        this.workInProgress = [];

        for (let i = 0; i < MaxWorkers; i++) {
            const worker = this.startWorker(i);
            this.workers.push(worker);
            this.workerQueue.push(worker);
            this.workInProgress.push(null);
        }
    }

    private process() {
        //console.log(`Timeline queue has ${this.workQueue.length} items; ${Object.keys(this.workers).length} of ${MaxWorkers} active workers`);

        // Get the next item of work
        const work = this.workQueue.shift();
        if (!work) {
            // Bail out because there is no work to perform
            return;
        }
        const { cellIndex, callback, ...input } = work;

        // Get the next available worker
        const worker = this.workerQueue.shift();
        if (!worker) {
            // Bail out because no available workers (but first return the work to the queue)
            this.workQueue.unshift(work);
            return;
        }
        
        // Start the work
        this.workInProgress[worker.index] = work;
        worker.postMessage(input);
    }

    private onWorkComplete(index: number) {
        const wip = this.workInProgress[index] as WorkWithCallback;
        this.workInProgress[index] = null;
        this.workerQueue.push(this.workers[index]);
        this.process();
        return wip;
    }

    private startWorker(index: number) {
        //console.log(`Starting worker ${index}`);

        const indexString = `${index}`;
        const worker = factory(indexString) as TimelineWorker;
        worker.index = index;

        // The worker functions are called in the context of the worker object
        // So we need to preserve the correct 'this' context and re-assert it
        // inside the callbacks.
        const thisPool = this;

        worker.onerror = (e: ErrorEvent) => {
            const wip = thisPool.onWorkComplete.call(thisPool, index);
            if (wip) {
                wip.callback(e.error);
            }
        };

        worker.onmessage = (e: MessageEvent) => {
            const wip = thisPool.onWorkComplete.call(thisPool, index);
            if (wip) {
                wip.callback(null, e.data);
            }
        };

        return worker as TimelineWorker;
    }

    queueWork(work: Work, callback: Callback) {
        this.workQueue.push({ ...work, callback });
        this.process();
    }
}

// Running this code in IE will cause the whole app to break.
// So return a dummy null (that never gets accessed) in IE
export const timelineWorkerPoolFactory = (): TimelineWorkerPool | null =>
    /MSIE|Trident/gi.test(navigator.userAgent)
        ? null
        : new TimelineWorkerPool();



// WEBPACK FOOTER //
// ./src/workers/timeline/timelineWorkerPool.ts